import { Suspense, lazy, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { List, Filter } from 'lucide-react';
import { Spinner } from '@brainsway/ui';
import { cn } from '@brainsway/ui';
import { useMapHospitals, useMyHospitals } from '../hooks';
import { usePermissions } from '@/features/auth/hooks/usePermissions';

const HospitalMap = lazy(() =>
  import('../components/HospitalMap').then((m) => ({ default: m.HospitalMap }))
);

export function HospitalMapPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: hospitals, isLoading } = useMapHospitals();
  const { data: myHospitals } = useMyHospitals();
  const { canAccessAdmin } = usePermissions();
  const isTherapist = !canAccessAdmin();

  const [selectedCity, setSelectedCity] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const myHospitalIds = useMemo(
    () => new Set((myHospitals ?? []).map((h) => h.id)),
    [myHospitals]
  );

  const cities = useMemo(() => {
    if (!hospitals) return [];
    const set = new Set(hospitals.map((h) => h.city));
    return Array.from(set).sort();
  }, [hospitals]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  const hospitalList = hospitals ?? [];
  const visibleCount = selectedCity
    ? hospitalList.filter((h) => h.city === selectedCity).length
    : hospitalList.length;

  return (
    <div className="flex h-[calc(100dvh-8rem)] flex-col gap-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg sm:text-2xl font-semibold tracking-wide">
            {t('hospitals.map', 'Hospital Map')}
          </h1>
          <p className="text-xs sm:text-sm text-slate-500">
            {t('hospitals.mapDescription', 'View all hospital locations')}
            {' '}&middot; {visibleCount} {t('hospitals.hospitalsCount', 'hospitals')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={cn(
              'flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm',
              selectedCity
                ? 'border-gray-200 bg-slate-50 text-[#0f172a] font-bold'
                : 'border-neutral-200 text-neutral-600 hover:bg-white/10'
            )}
          >
            <Filter className="h-4 w-4" />
            {t('audit.filters', 'Filters')}
          </button>
          <button
            onClick={() => navigate('/admin/hospitals')}
            className="flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-2 text-sm text-black hover:bg-white/10"
          >
            <List className="h-4 w-4" />
            {t('hospitals.listView', 'List View')}
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="glass-surface rounded-3xl border border-gray-200 p-3" style={{ backgroundColor: 'rgba(255, 255, 255, 0.45)' }}>
          <div className="flex flex-wrap items-center gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-neutral-600">
                {t('hospitals.city', 'City')}
              </label>
              <select
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                className="rounded-xl border border-gray-200 px-3 py-1.5 text-sm"
              >
                <option value="">{t('common.all', 'All')}</option>
                {cities.map((city) => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>
            {selectedCity && (
              <button
                onClick={() => setSelectedCity('')}
                className="mt-5 text-sm text-neutral-500 hover:text-neutral-700"
              >
                {t('audit.clearFilters', 'Clear')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-xs text-neutral-500">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-full bg-primary-500" />
          {t('hospitals.active', 'Active')}
        </span>
        {isTherapist && (
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 rounded-full bg-teal-500" />
            {t('hospitals.myHospital', 'My Hospital')}
          </span>
        )}
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-full bg-neutral-400" />
          {t('hospitals.inactive', 'Inactive')}
        </span>
      </div>

      {/* Map */}
      <div className="min-h-0 flex-1 overflow-hidden rounded-xl border border-gray-200">
        <Suspense
          fallback={
            <div className="flex h-full items-center justify-center bg-neutral-50">
              <Spinner size="lg" />
            </div>
          }
        >
          <HospitalMap
            hospitals={hospitalList}
            myHospitalIds={myHospitalIds}
            selectedCity={selectedCity}
          />
        </Suspense>
      </div>
    </div>
  );
}
